import React, { useState, useEffect } from 'react';
import { Company } from '../types/CompanyTypes';
import { companyDataService, CompanyDetailedInfo, JobListing } from '../services/companyDataService';
import { referralsService } from '../services/referrals.service';
import './CompanyDetailsPanel.css';
import './CompanyDetailsPanel.mobile.css';

interface CompanyDetailsPanelProps {
  company: Company | null;
  onClose: () => void;
  darkMode: boolean;
  onLoginRequired?: (action: 'apply' | 'save' | 'share' | 'referral' | string) => void;
  onReferralClick?: (companyId: string, companyName: string) => void;
}

const CompanyDetailsPanel: React.FC<CompanyDetailsPanelProps> = ({ company, onClose, darkMode, onLoginRequired, onReferralClick }) => {
  const [detailedInfo, setDetailedInfo] = useState<CompanyDetailedInfo | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'jobs' | 'reviews' | 'insights' | 'financial' | 'news'>('overview');
  const [loading, setLoading] = useState(false);
  const [isCompanySaved, setIsCompanySaved] = useState(false);
  
  // Helper function to get user ID from token
  const getUserIdFromToken = (): string | null => {
    const authToken = localStorage.getItem('jobometry_auth_token');
    if (!authToken) return null;
    
    try {
      const tokenParts = authToken.split('.');
      if (tokenParts.length !== 3) {
        // For development, create a mock user ID
        const mockUserId = 'user_' + Date.now();
        console.log('Using mock user ID:', mockUserId);
        return mockUserId;
      }
      
      const tokenData = JSON.parse(atob(tokenParts[1]));
      return tokenData.userId || tokenData.id || tokenData.sub || null;
    } catch (error) {
      // For development, create a mock user ID
      const mockUserId = 'user_' + Date.now();
      console.log('Using mock user ID:', mockUserId);
      return mockUserId;
    }
  };

  useEffect(() => {
    if (company) {
      loadCompanyDetails();
      // Check if company is saved
      if ((window as any).isCompanySaved) {
        setIsCompanySaved((window as any).isCompanySaved(company.id));
      }
    }
  }, [company]);
  
  // Listen for save state changes
  useEffect(() => {
    const checkSaveState = () => {
      if (company && (window as any).isCompanySaved) {
        setIsCompanySaved((window as any).isCompanySaved(company.id));
      }
    };
    
    // Check periodically for save state changes
    const interval = setInterval(checkSaveState, 500);
    
    return () => clearInterval(interval);
  }, [company]);

  const loadCompanyDetails = async () => {
    if (!company) return;
    
    setLoading(true);
    try {
      const details = await companyDataService.getCompanyDetailedInfo(company.id);
      setDetailedInfo(details);
    } catch (error) {
      console.error('Failed to load company details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <span className="star-rating">
        {'★'.repeat(fullStars)}
        {hasHalfStar && '☆'}
        {'☆'.repeat(emptyStars)}
      </span>
    );
  };

  const renderMetricBar = (label: string, value: number, icon: string) => {
    return (
      <div className="metric-item">
        <div className="metric-header">
          <span className="material-icons metric-icon">{icon}</span>
          <span className="metric-label">{label}</span>
          <span className="metric-value">{value.toFixed(1)}</span>
        </div>
        <div className="metric-bar">
          <div 
            className="metric-fill" 
            style={{ width: `${(value / 5) * 100}%` }}
          />
        </div>
      </div>
    );
  };

  console.log('CompanyDetailsPanel render - company:', company?.name, 'darkMode:', darkMode);
  
  return (
    <div className={`company-details-panel ${darkMode ? 'dark' : 'light'} ${company ? 'open' : ''}`}>
      {company && (
        <>
          <div className="panel-header">
            <div className="company-header">
              <div className="company-logo-placeholder">
                {company.name.charAt(0)}
              </div>
              <div className="company-title">
                <h2>{company.name}</h2>
                <div className="company-meta">
                  <span className="industry">{company.industry}</span>
                  <span className="separator">•</span>
                  <span className="size">{company.size}</span>
                  <span className="separator">•</span>
                  <span className="founded">Founded {company.foundedYear}</span>
                </div>
              </div>
              <div className="overall-rating">
                <div className="rating-number">{company.globalRating.toFixed(1)}</div>
                {renderStars(company.globalRating)}
                <div className="rating-count">({detailedInfo?.reviewCount || 0} reviews)</div>
              </div>
            </div>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

      <div className="tabs-container">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Jobs ({detailedInfo?.jobListings?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => setActiveTab('reviews')}
        >
          Reviews
        </button>
        <button 
          className={`tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          Insights
        </button>
        {detailedInfo?.stockData && (
          <button 
            className={`tab ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            📊 Financial
          </button>
        )}
        <button 
          className={`tab ${activeTab === 'news' ? 'active' : ''}`}
          onClick={() => setActiveTab('news')}
        >
          📰 News
        </button>
      </div>

      <div className="panel-content">
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading company information...</p>
          </div>
        )}

        {!loading && activeTab === 'overview' && (
          <div className="overview-content">
            <div className="company-highlights">
              <div className="highlight-card">
                <h3><span className="material-icons">payments</span> Compensation</h3>
                <div className="highlight-value">{detailedInfo?.avgSalary ? formatSalary(detailedInfo.avgSalary) : 'N/A'}</div>
                <div className="highlight-label">Average Salary</div>
              </div>
              
              <div className="highlight-card">
                <h3><span className="material-icons">trending_up</span> Growth</h3>
                <div className={`highlight-value ${detailedInfo?.growthRate && detailedInfo.growthRate > 0 ? 'positive' : 'negative'}`}>
                  {detailedInfo?.growthRate ? `${detailedInfo.growthRate > 0 ? '+' : ''}${detailedInfo.growthRate.toFixed(1)}%` : 'N/A'}
                </div>
                <div className="highlight-label">Annual Growth</div>
              </div>
              
              <div className="highlight-card">
                <h3><span className="material-icons">groups</span> Workforce</h3>
                <div className="highlight-value">{detailedInfo?.employeeCount ? detailedInfo.employeeCount.toLocaleString() : company.size}</div>
                <div className="highlight-label">Employees</div>
              </div>
              
              <div className="highlight-card">
                <h3><span className="material-icons">business</span> Opportunities</h3>
                <div className="highlight-value">{detailedInfo?.jobListings?.length || 0}</div>
                <div className="highlight-label">Open Positions</div>
              </div>
            </div>

            {detailedInfo?.hasLayoffs && (
              <div className="alert-banner layoff-alert">
                <span className="material-icons alert-icon">warning</span>
                <div className="alert-content">
                  <strong>Recent Workforce Reduction</strong>
                  <p>This company has recently reduced its workforce by {detailedInfo.layoffPercentage?.toFixed(1)}%</p>
                </div>
              </div>
            )}

            <div className="metrics-section">
              <h3>Company Ratings Breakdown</h3>
              <div className="metrics-grid">
                {detailedInfo?.metrics && (
                  <>
                    {renderMetricBar('Work-Life Balance', detailedInfo.metrics.workLifeBalance, 'balance')}
                    {renderMetricBar('Career Opportunities', detailedInfo.metrics.careerOpportunities, 'trending_up')}
                    {renderMetricBar('Compensation & Benefits', detailedInfo.metrics.compensationBenefits, 'payments')}
                    {renderMetricBar('Senior Management', detailedInfo.metrics.seniorManagement, 'manage_accounts')}
                    {renderMetricBar('Culture & Values', detailedInfo.metrics.cultureValues, 'handshake')}
                  </>
                )}
              </div>
            </div>

            {detailedInfo && (
              <>
                <div className="about-section">
                  <h3>About {company.name}</h3>
                  <p>{detailedInfo.description}</p>
                  
                  <div className="company-info-grid">
                    <div className="info-item">
                      <span className="info-label">Headquarters</span>
                      <span className="info-value">{detailedInfo.headquarters}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">CEO</span>
                      <span className="info-value">{detailedInfo.ceo}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Website</span>
                      <a href={detailedInfo.website} target="_blank" rel="noopener noreferrer" className="info-value link">
                        {detailedInfo.website}
                      </a>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Stock Symbol</span>
                      <span className="info-value">{detailedInfo.stockSymbol || 'Private'}</span>
                    </div>
                  </div>
                </div>

                <div className="benefits-section">
                  <h3>Benefits & Perks</h3>
                  <div className="benefits-grid">
                    {detailedInfo.benefits.map((benefit, index) => (
                      <div key={index} className="benefit-item">
                        <span className="benefit-icon material-icons">{benefit.icon}</span>
                        <span className="benefit-name">{benefit.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && activeTab === 'jobs' && (
          <div className="jobs-content">
            <div className="jobs-header">
              <h3>Open Positions at {company.name}</h3>
              <div className="job-filters">
                <select className="filter-select">
                  <option>All Departments</option>
                  <option>Engineering</option>
                  <option>Product</option>
                  <option>Sales</option>
                  <option>Marketing</option>
                </select>
                <select className="filter-select">
                  <option>All Locations</option>
                  <option>Remote</option>
                  <option>On-site</option>
                  <option>Hybrid</option>
                </select>
              </div>
            </div>
            
            <div className="jobs-list">
              {detailedInfo?.jobListings.map((job) => (
                <div key={job.id} className="job-card">
                  <div className="job-header">
                    <h4>{job.title}</h4>
                    <span className="job-salary">{formatSalary(job.salaryMin)} - {formatSalary(job.salaryMax)}</span>
                  </div>
                  <div className="job-meta">
                    <span className="job-department">{job.department}</span>
                    <span className="separator">•</span>
                    <span className="job-location">{job.location}</span>
                    <span className="separator">•</span>
                    <span className="job-type">{job.type}</span>
                  </div>
                  <p className="job-description">{job.description}</p>
                  
                  {/* Income Breakdown Section */}
                  <div className="job-income-breakdown">
                    <h5 className="breakdown-title">
                      <span className="material-icons">account_balance_wallet</span>
                      Income Breakdown
                    </h5>
                    <div className="income-stats">
                      <div className="income-stat">
                        <span className="stat-label">Gross Annual</span>
                        <span className="stat-value">{formatSalary((job.salaryMin + job.salaryMax) / 2)}</span>
                      </div>
                      <div className="income-stat">
                        <span className="stat-label">Tax Rate</span>
                        <span className="stat-value">~30%</span>
                      </div>
                      <div className="income-stat">
                        <span className="stat-label">Net Monthly</span>
                        <span className="stat-value">{formatSalary(((job.salaryMin + job.salaryMax) / 2 * 0.7) / 12)}</span>
                      </div>
                    </div>
                    
                    <div className="expense-breakdown">
                      <div className="expense-item">
                        <span className="expense-icon material-icons">home</span>
                        <span className="expense-label">Rent (35%)</span>
                        <span className="expense-value">{formatSalary(((job.salaryMin + job.salaryMax) / 2 * 0.7 * 0.35) / 12)}</span>
                      </div>
                      <div className="expense-item">
                        <span className="expense-icon material-icons">restaurant</span>
                        <span className="expense-label">Food (15%)</span>
                        <span className="expense-value">{formatSalary(((job.salaryMin + job.salaryMax) / 2 * 0.7 * 0.15) / 12)}</span>
                      </div>
                      <div className="expense-item">
                        <span className="expense-icon material-icons">directions_car</span>
                        <span className="expense-label">Transport (10%)</span>
                        <span className="expense-value">{formatSalary(((job.salaryMin + job.salaryMax) / 2 * 0.7 * 0.10) / 12)}</span>
                      </div>
                      <div className="expense-item highlight">
                        <span className="expense-icon material-icons">savings</span>
                        <span className="expense-label">Savings (22%)</span>
                        <span className="expense-value">{formatSalary(((job.salaryMin + job.salaryMax) / 2 * 0.7 * 0.22) / 12)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="job-actions">
                    <button 
                      className="apply-btn"
                      onClick={() => onLoginRequired ? onLoginRequired('apply') : null}
                    >
                      Apply Now
                    </button>
                    <button 
                      className="save-btn"
                      onClick={() => onLoginRequired ? onLoginRequired('save') : null}
                    >
                      Save Job
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && activeTab === 'reviews' && (
          <div className="reviews-content">
            <div className="reviews-summary">
              <h3>Employee Reviews</h3>
              <div className="review-stats">
                <div className="recommend-stat">
                  <span className="stat-percentage">{detailedInfo?.recommendToFriend || 0}%</span>
                  <span className="stat-label">Recommend to a friend</span>
                </div>
                <div className="ceo-approval-stat">
                  <span className="stat-percentage">{detailedInfo?.ceoApproval || 0}%</span>
                  <span className="stat-label">Approve of CEO</span>
                </div>
              </div>
            </div>
            
            <div className="reviews-list">
              {detailedInfo?.reviews.map((review, index) => (
                <div key={index} className="review-card">
                  <div className="review-header">
                    <span className="review-title">{review.title}</span>
                    <span className="review-rating">{renderStars(review.rating)}</span>
                  </div>
                  <div className="review-meta">
                    <span className="review-role">{review.role}</span>
                    <span className="separator">•</span>
                    <span className="review-date">{review.date}</span>
                    <span className="separator">•</span>
                    <span className="review-status">{review.currentEmployee ? 'Current Employee' : 'Former Employee'}</span>
                  </div>
                  <div className="review-content">
                    <div className="pros">
                      <strong>Pros:</strong>
                      <p>{review.pros}</p>
                    </div>
                    <div className="cons">
                      <strong>Cons:</strong>
                      <p>{review.cons}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && activeTab === 'insights' && (
          <div className="insights-content">
            <h3>Market Insights</h3>
            
            <div className="insight-cards">
              <div className="insight-card">
                <h4>🎯 Competitive Position</h4>
                <p>This company ranks in the top {detailedInfo?.industryRankPercentile || 0}% of {company.industry} companies for employee satisfaction.</p>
              </div>
              
              <div className="insight-card">
                <h4>📊 Salary Comparison</h4>
                <p>Salaries at {company.name} are {detailedInfo?.salaryVsMarket || 0}% {(detailedInfo?.salaryVsMarket || 0) > 0 ? 'above' : 'below'} market average for similar roles.</p>
              </div>
              
              <div className="insight-card">
                <h4>🌟 Top Skills in Demand</h4>
                <div className="skills-list">
                  {detailedInfo?.topSkills.map((skill, index) => (
                    <span key={index} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
              
              <div className="insight-card">
                <h4>📈 Hiring Trends</h4>
                <p>Job postings have {detailedInfo?.growthRate && detailedInfo.growthRate > 0 ? 'increased' : 'decreased'} by {Math.abs(detailedInfo?.growthRate || 0)}% over the past year.</p>
              </div>
            </div>

            {detailedInfo?.competitors && (
              <div className="competitors-section">
                <h4>How {company.name} Compares</h4>
                <div className="competitors-table">
                  <div className="table-header">
                    <span>Company</span>
                    <span>Rating</span>
                    <span>Avg Salary</span>
                    <span>Jobs</span>
                  </div>
                  {detailedInfo.competitors.map((competitor, index) => (
                    <div key={index} className="table-row">
                      <span className={competitor.name === company.name ? 'highlight' : ''}>{competitor.name}</span>
                      <span className={competitor.name === company.name ? 'highlight' : ''}>{competitor.rating.toFixed(1)}</span>
                      <span className={competitor.name === company.name ? 'highlight' : ''}>{formatSalary(competitor.avgSalary)}</span>
                      <span className={competitor.name === company.name ? 'highlight' : ''}>{competitor.openJobs}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'financial' && detailedInfo?.stockData && (
          <div className="financial-content">
            <h3>📊 Financial Information</h3>
            
            <div className="stock-overview">
              <div className="stock-header">
                <h4>{detailedInfo.stockData.symbol}</h4>
                <div className="stock-price">
                  <span className="price">${detailedInfo.stockData.price.toFixed(2)}</span>
                  <span className={`change ${detailedInfo.stockData.change >= 0 ? 'positive' : 'negative'}`}>
                    {detailedInfo.stockData.change >= 0 ? '+' : ''}{detailedInfo.stockData.change.toFixed(2)} 
                    ({detailedInfo.stockData.changePercent >= 0 ? '+' : ''}{detailedInfo.stockData.changePercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
              
              <div className="financial-metrics">
                <div className="metric-card">
                  <span className="metric-label">Volume</span>
                  <span className="metric-value">{detailedInfo.stockData.volume.toLocaleString()}</span>
                </div>
                {detailedInfo.stockData.marketCap > 0 && (
                  <div className="metric-card">
                    <span className="metric-label">Market Cap</span>
                    <span className="metric-value">${(detailedInfo.stockData.marketCap / 1000000000).toFixed(1)}B</span>
                  </div>
                )}
                <div className="metric-card">
                  <span className="metric-label">Last Updated</span>
                  <span className="metric-value">{new Date(detailedInfo.stockData.lastUpdated).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {detailedInfo.levelsData?.compensationTrends && (
              <div className="compensation-trends">
                <h4>💰 Compensation Trends</h4>
                <div className="trend-chart">
                  {detailedInfo.levelsData.compensationTrends.map((trend, index) => (
                    <div key={index} className="trend-bar">
                      <span className="trend-year">{trend.year}</span>
                      <div className="trend-bar-container">
                        <div 
                          className="trend-bar-fill" 
                          style={{ width: `${(trend.averageCompensation / 200000) * 100}%` }}
                        />
                      </div>
                      <span className="trend-value">{formatSalary(trend.averageCompensation)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detailedInfo.crunchbaseData && (
              <div className="crunchbase-section">
                <h4>🚀 Funding & Investment</h4>
                <div className="funding-stats">
                  <div className="funding-card">
                    <span className="funding-label">Total Funding</span>
                    <span className="funding-value">${(detailedInfo.crunchbaseData.totalFunding / 1000000).toFixed(1)}M</span>
                  </div>
                  <div className="funding-card">
                    <span className="funding-label">Last Round</span>
                    <span className="funding-value">${(detailedInfo.crunchbaseData.lastFundingAmount / 1000000).toFixed(1)}M</span>
                  </div>
                  <div className="funding-card">
                    <span className="funding-label">Founded</span>
                    <span className="funding-value">{new Date(detailedInfo.crunchbaseData.foundingDate).getFullYear()}</span>
                  </div>
                </div>
                
                {detailedInfo.crunchbaseData.investors.length > 0 && (
                  <div className="investors-section">
                    <strong>Key Investors:</strong>
                    <div className="investors-list">
                      {detailedInfo.crunchbaseData.investors.slice(0, 5).map((investor, index) => (
                        <span key={index} className="investor-tag">{investor}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'news' && (
          <div className="news-content">
            <h3>📰 Recent News & Updates</h3>
            
            {detailedInfo?.recentNews && detailedInfo.recentNews.length > 0 ? (
              <div className="news-list">
                {detailedInfo.recentNews.map((news, index) => (
                  <div key={index} className="news-article">
                    <div className="news-header">
                      <h4>
                        <a href={news.url} target="_blank" rel="noopener noreferrer">
                          {news.title}
                        </a>
                      </h4>
                      <div className="news-meta">
                        <span className="news-source">{news.source}</span>
                        <span className="news-date">
                          {new Date(news.publishedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <p className="news-summary">{news.summary}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-news">
                <p>📰 No recent news available for this company.</p>
                <p>Check back later for updates on company announcements, product launches, and industry insights.</p>
              </div>
            )}

            {detailedInfo?.jobMarketData && (
              <div className="hiring-trends-section">
                <h4>📈 Hiring Trends</h4>
                <div className="trend-summary">
                  <div className="trend-stat">
                    <span className="trend-icon">📊</span>
                    <div className="trend-info">
                      <span className="trend-label">Hiring Trend</span>
                      <span className={`trend-value ${detailedInfo.jobMarketData.hiringTrend}`}>
                        {detailedInfo.jobMarketData.hiringTrend.charAt(0).toUpperCase() + detailedInfo.jobMarketData.hiringTrend.slice(1)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="trend-stat">
                    <span className="trend-icon">⏱️</span>
                    <div className="trend-info">
                      <span className="trend-label">Avg. Time to Hire</span>
                      <span className="trend-value">{detailedInfo.jobMarketData.averageTimeToHire} days</span>
                    </div>
                  </div>
                  
                  <div className="trend-stat">
                    <span className="trend-icon">💼</span>
                    <div className="trend-info">
                      <span className="trend-label">Total Openings</span>
                      <span className="trend-value">{detailedInfo.jobMarketData.totalOpenings}</span>
                    </div>
                  </div>
                </div>

                <div className="department-growth">
                  <strong>Department Growth:</strong>
                  <div className="growth-bars">
                    {detailedInfo.jobMarketData.departmentGrowth.map((dept, index) => (
                      <div key={index} className="growth-item">
                        <span className="dept-name">{dept.department}</span>
                        <div className="growth-bar">
                          <div 
                            className="growth-fill" 
                            style={{ width: `${Math.min(dept.growthRate * 4, 100)}%` }}
                          />
                        </div>
                        <span className="growth-value">+{dept.growthRate}%</span>
                        <span className="openings-count">({dept.openings} jobs)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer Actions */}
      <div className="panel-footer">
        <div className="footer-actions">
          <button 
            className={`save-company-btn ${isCompanySaved ? 'saved' : ''}`}
            onClick={() => {
              console.log('Save company button clicked for:', company);
              // Call the global save function which will handle login check
              if ((window as any).toggleSaveCompany) {
                console.log('Calling toggleSaveCompany with:', company.id, company);
                (window as any).toggleSaveCompany(company.id, company);
              } else {
                console.error('toggleSaveCompany function not found on window');
              }
            }}
          >
            <span className="material-icons">{isCompanySaved ? 'bookmark' : 'bookmark_border'}</span>
            {isCompanySaved ? 'Saved' : 'Save Company'}
          </button>
          <button 
            className="referral-btn"
            onClick={async () => {
              console.log('Referral button clicked for company:', company);
              
              if (!company) {
                alert('Company information not available');
                return;
              }
              
              const authToken = localStorage.getItem('jobometry_auth_token');
              
              // Check if user is logged in first
              if (!authToken || authToken === 'null' || authToken === 'undefined') {
                onLoginRequired && onLoginRequired('referral');
                return;
              }
              
              const userId = getUserIdFromToken();
              if (!userId) {
                onLoginRequired && onLoginRequired('referral');
                return;
              }
              
              try {
                console.log('Submitting referral for user:', userId, 'company:', company.id, company.name);
                
                // Create a referral (expressing interest)
                await referralsService.submitReferral(
                  userId,
                  company.id,
                  company.name,
                  'I am interested in working at this company'
                );
                
                alert(`You've expressed interest in working at ${company.name}!`);
              } catch (error) {
                console.error('Error submitting referral:', error);
                alert('Failed to submit your interest. Please try again.');
              }
            }}
          >
            <span className="material-icons">person_add</span>
            I Want to Work Here
          </button>
          <button 
            className="share-btn"
            onClick={() => {
              if (navigator.share && company) {
                navigator.share({
                  title: `${company.name} - Company Profile`,
                  text: `Check out this company on Jobometry`,
                  url: window.location.href
                }).catch(err => console.log('Error sharing:', err));
              } else {
                // Fallback to copy link
                navigator.clipboard.writeText(window.location.href)
                  .then(() => alert('Link copied to clipboard!'))
                  .catch(() => alert('Failed to copy link'));
              }
            }}
          >
            <span className="material-icons">share</span>
            Share
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default CompanyDetailsPanel;